import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { List } from "immutable";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import DiscussionForm from "~/components/Discussions/DiscussionForm";
import { addDiscussion, editDiscussion } from "~/store/actions/actions.discussions";
import { updateSubscribers } from "~/store/actions/actions.subscription";
import { getRawConfigOption } from "~/store/selectors/selector.configOptions";
import { getDiscussionById } from "~/store/selectors/selector.discussions";
import { getInstanceId } from "~/store/selectors/selector.instance";
import {
  getProjectSubscriberIds,
  getSortedSubscribersByProjectId,
  getSubscriberIdsByTypeAndId,
} from "~/store/selectors/selector.subscription";
import { getLoggedUserId, isLoggedUserClientSelector } from "~/store/selectors/selector.users";
import { useDraft } from "../Hooks/useDraft";
import { showSuccessToast, showErrorToast } from "../Toast";

const DiscussionFormContainer = ({
  discussionId,
  projectId,
  onClose,
  submitButtonLabel,
  shouldOverflow,
  className,
}) => {
  const [draft, setDraft] = useDraft("discussion");
  const loggedUserId = useSelector(getLoggedUserId);
  const instanceId = useSelector(getInstanceId);
  const draftId = discussionId
    ? `instance_${instanceId}_user_${loggedUserId}_${projectId}_${discussionId}`
    : `instance_${instanceId}_user_${loggedUserId}_${projectId}`;
  const hasDraft = !!(draft && draft[draftId]);

  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);
  const defaultHideFromClients = useSelector((state) => getRawConfigOption(state, "default_hide_from_clients"));
  const hideFromClients = useSelector((state) => getRawConfigOption(state, "hide_from_clients_enabled"));

  const defaultHiddenFromClients = isLoggedUserClient ? false : defaultHideFromClients;

  // on existing discussion
  const discussionSubscriberIds = useSelector(
    (state) => getSubscriberIdsByTypeAndId(state, "Discussion", discussionId),
    isEqual
  );

  // before discussion exist
  const initialSubscriberIds = useSelector((state) => getProjectSubscriberIds(state, projectId));

  const discussion = useSelector((state) => getDiscussionById(state, discussionId));

  const projectMembers = useSelector((state) => getSortedSubscribersByProjectId(state, projectId));

  const handleSaveDraft = useCallback(
    (attributes) => {
      setDraft((prevState) => {
        return {
          ...prevState,
          [draftId]: attributes,
        };
      });
    },
    [draftId, setDraft]
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleRemoveDraft = useCallback(() => {
    const { [draftId]: current, ...rest } = draft;
    setDraft(rest);
  }, [draftId, setDraft, draft]);

  const handleClose = useCallback(() => {
    const promise = new Promise((resolve) => {
      setDraft((prevState) => {
        if (!prevState) return undefined;
        const { [draftId]: current, ...rest } = prevState;
        return rest;
      });
      setTimeout(() => {
        resolve(true);
      }, 100);
    });
    promise.then(() => {
      if (typeof onClose === "function") {
        onClose();
      }
    });
  }, [draftId, onClose, setDraft]);

  const handleSubmit = useCallback(
    async (attributes) => {
      handleRemoveDraft();
      if (discussionId) {
        const first = [...attributes.subscribers].sort();
        const second = [...discussionSubscriberIds].sort();
        if (!isEqual(first, second)) {
          await dispatch(updateSubscribers(discussionId, "discussion", attributes.subscribers));
        }
        await dispatch(editDiscussion(projectId, discussionId, attributes));
      } else {
        const action = await dispatch(addDiscussion(projectId, attributes));
        if (action.type === "ADD_DISCUSSION_SUCCESS") {
          navigate(`?modal=Discussion-${action.response.result.single}-${projectId}`);
          showSuccessToast(t("Discussion created"));
        } else {
          showErrorToast(t("Failed to create discussion"));
        }
      }
      handleClose();
    },
    [handleRemoveDraft, discussionId, handleClose, discussionSubscriberIds, dispatch, projectId, navigate, t]
  );

  const formData = useMemo(() => {
    let object = {};
    if (hasDraft) {
      object = { ...draft[draftId], project_id: projectId };
    } else {
      if (discussionId && discussion) {
        object = {
          ...discussion,
          project_id: projectId,
          subscribers: discussionSubscriberIds,
        };
      }
    }
    return object;
  }, [discussion, discussionId, draft, draftId, hasDraft, discussionSubscriberIds, projectId]);

  const {
    id,
    name,
    body,
    is_pinned,
    is_hidden_from_clients = defaultHiddenFromClients,
    subscribers = initialSubscriberIds,
    drop_attached_files,
    attach_uploaded_files,
  } = formData;

  return (
    <DiscussionForm
      projectId={projectId}
      onChange={handleSaveDraft}
      onSubmit={handleSubmit}
      hasDraft={hasDraft}
      id={id}
      name={name}
      body={body}
      isPinned={is_pinned}
      isHiddenFromClients={is_hidden_from_clients}
      subscribers={subscribers}
      onCancel={handleClose}
      submitButtonLabel={submitButtonLabel}
      isLoggedUserClient={isLoggedUserClient}
      attachments={new List(discussion?.attachments)}
      dropAttachedFiles={drop_attached_files}
      attachUploadedFiles={attach_uploaded_files}
      hideFromClients={hideFromClients}
      className={className}
      projectMembers={projectMembers}
      shouldOverflow={shouldOverflow}
    />
  );
};

DiscussionFormContainer.propTypes = {
  projectId: PropTypes.number.isRequired,
  discussionId: PropTypes.number,
  submitButtonLabel: PropTypes.string,
  onClose: PropTypes.func,
  className: PropTypes.string,
  shouldOverflow: PropTypes.bool,
};

export default DiscussionFormContainer;
